
/**
 * Flattens the keys of an object
 * @example
 * const obj = { a: { b: { c: 'd' } } }
 * const flat = flattenKeys(obj) // { 'a.b.c': 'd' }
 * @param {object} obj
 * @param {string} [prefix=null] - The prefix to use for the keys
 * @returns {object}
 */
export function flattenKeys(obj, prefix = null) {
  const prefixKey = prefix ? `${prefix}.` : ''
  return Object.keys(obj).reduce((acc, key) => {
    const newKey = `${prefixKey}${key}`
    const value = obj[key]
    if (typeof value === 'object') {
      return { ...acc, ...flattenKeys(value, newKey) }
    }
    return { ...acc, [newKey]: value }
  }, {})
}

/**
 * Recursively converts the given value and its children using the callback.
 * @param {any} value
 * @param {{
 *  callback?: (any) => any - The callback to call on the value and its children
 *  omitFunctions?: boolean - Whether to omit functions from the clone. Defaults to false.
 * }} options - The options to use for the clone
 * @param {WeakMap<any, any>} seen - The map of seen values to avoid circular references
 * @returns {any}
 */
export function cloneValue(value, options = {}, seen = new WeakMap()) {
  if (value === null || typeof value !== 'object') {
    return value
  }
  // Handle potential circular references
  if (seen.has(value)) {
    return seen.get(value)
  }

  const { callback = null, omitFunctions = false } = options

  const isObject = Object.prototype.toString.call(value) === '[object Object]'
  const isArray = Array.isArray(value)
  const converted = callback ? callback(value) : value
  const convertedValue = isObject ? {} : isArray ? [] : converted
  seen.set(value, convertedValue)

  if (isObject || isArray) {
    for (const key in converted) {
      const clonedValue = cloneValue(converted[key], options, seen)
      if (omitFunctions && typeof clonedValue === 'function') {
        continue
      }
      convertedValue[key] = clonedValue
    }
  }
  return convertedValue
}
